// E-postadan kişi kimliği (ad soyad + profil fotoğrafı) çözümü.
//
// Mesaj bildirimleri ve aramalar kişiyi yalnızca e-postasıyla tanır. Ad soyad
// `Employees` koleksiyonunda tutulur; `Users` eski/ikincil kaynaktır ve bazı
// kayıtlarda hiç bulunmaz. Önceden yalnızca `Users` sorgulandığı için isim
// çözülemiyor ve ekranlarda ham e-posta görünüyordu.

import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';

// E-posta → { name, email, image }. Aynı oturumda tekrar sorulmaz.
const identityCache = new Map();

const NAME_SOURCES = ['Employees', 'Users'];

export const isEmailLike = (value) => typeof value === 'string' && value.includes('@');

/**
 * Kayıt bulunamadığında e-postadan okunabilir bir ad üretir:
 * "arzu.dereli@habcarecro.com" → "Arzu Dereli".
 * Ham e-posta göstermekten her zaman daha iyi; gerçek e-posta arayüzde zaten
 * ikincil satırda gösterilir.
 */
export const prettifyEmail = (email) => {
  if (!isEmailLike(email)) return email || '';
  const [localPart] = email.split('@');
  const words = localPart
    .replace(/[._-]+/g, ' ')
    .replace(/\d+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return email;
  return words
    .map((word) => word.charAt(0).toLocaleUpperCase('tr-TR') + word.slice(1))
    .join(' ');
};

/** Ad soyad (yoksa e-postadan türetilmiş ad) — asla ham e-posta dönmez. */
export const displayNameOf = (identity) =>
  identity?.name || prettifyEmail(identity?.email) || 'Bilinmeyen kullanıcı';

/**
 * E-postaya ait kişiyi çözer. Önce Employees, sonra Users denenir; ikisinde de
 * bulunamazsa ad e-postadan türetilir.
 */
export const resolveUserIdentity = async (email) => {
  if (!email) return { name: '', email: '', image: null };

  const key = String(email).toLowerCase();
  if (identityCache.has(key)) return identityCache.get(key);

  let identity = { name: '', email, image: null };

  try {
    for (const source of NAME_SOURCES) {
      const snapshot = await getDocs(
        query(collection(db, source), where('eMail', '==', email))
      );
      const data = snapshot.docs[0]?.data();
      if (!data) continue;

      const name = [data.eName, data.eSurname].filter(Boolean).join(' ').trim();
      if (name) {
        identity = { name, email, image: data.profileImageUrl || null };
        break;
      }
    }
  } catch (error) {
    console.debug('Kişi bilgisi çözülemedi:', error);
  }

  identityCache.set(key, identity);
  return identity;
};

/**
 * Kayıtlarda saklanmış bir "ad" alanını görüntülemeye hazırlar.
 * Eski kayıtlarda bu alana e-posta yazılmış olabilir; o durumda e-postadan
 * okunabilir ad üretilir.
 */
export const normalizeStoredName = (storedName, email) => {
  if (storedName && !isEmailLike(storedName)) return storedName;
  return prettifyEmail(storedName || email) || 'Bilinmeyen kullanıcı';
};
